import googleLoader from 'google-maps';
import {Address} from '@/modules/multiForm/shared/MultiformModels';
import { intersection } from 'lodash-es';
import GoogleAutocompletePrediction from '@/modules/googleAutocomplete/GoogleAutocompletePrediction';
import {AutocompletePlacesTypes} from '@/modules/googleAutocomplete/enums/AutocompletePlacesTypes';
import GooglePlacesMapper from '@/modules/googleAutocomplete/services/GooglePlacesMapper';
import {GooglePlacesTypes} from '@/modules/googleAutocomplete/enums/GooglePlacesTypes';
import EnvironmentService from '@/env/EnvironmentService';
import { CountryCode } from 'libphonenumber-js/types';

googleLoader.KEY = 'AIzaSyBdjik8gQXgHsIWoev_rvgCdKaf14Zhztg';
googleLoader.LIBRARIES = ['places',];

const appDomainConfig = EnvironmentService.Environment.getAppDomainConfig();
googleLoader.LANGUAGE = appDomainConfig.region.toUpperCase();
googleLoader.REGION = appDomainConfig.region.toUpperCase();

interface IGooglePromise{
  AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion
  AutocompleteSessionToken: typeof google.maps.places.AutocompleteSessionToken
  Geocoder: google.maps.Geocoder
}

const GooglePromise = new Promise<IGooglePromise>(resolve => {
  googleLoader.load((googleInstance) => {
    resolve({
      AutocompleteSuggestion: googleInstance.maps.places.AutocompleteSuggestion,
      AutocompleteSessionToken: googleInstance.maps.places.AutocompleteSessionToken,
      Geocoder: new googleInstance.maps.Geocoder(),
    });
  });
});

class GooglePlacesApi {
  private googleApi!: IGooglePromise;
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  constructor() {
    GooglePromise.then(x => {
      this.googleApi = x;
    });
  }

  private getSessionToken(): google.maps.places.AutocompleteSessionToken {
    if (!this.sessionToken) {
      this.sessionToken = new this.googleApi.AutocompleteSessionToken();
    }
    return this.sessionToken;
  }

  private resetSessionToken(): void {
    this.sessionToken = null;
  }

  private async getPlaceDetails(placeId: string): Promise<google.maps.GeocoderResult> {
    return new Promise<google.maps.GeocoderResult>((resolve) => {
      this.googleApi.Geocoder.geocode({placeId, }, results => resolve(results[0]));
    });
  };

  public async getAddress(placeId: string, description?: string): Promise<Address> {
    const response = await this.getPlaceDetails(placeId);
    this.resetSessionToken();
    const foundPlace = GooglePlacesMapper.findPlace(response);
    return new Address({
      landId: foundPlace?.landId || null,
      districtId: foundPlace?.districtId || null,
      country: GooglePlacesMapper.findComponentShortName(response, GooglePlacesTypes.COUNTRY),
      street: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.ROUTE),
      city: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.LOCALITY),
      cityDistrict: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.SUBLOCALITY),
      streetNum: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.STREET_NUMBER),
      streetLocal: GooglePlacesMapper.findStreetLocal(response, description),
      zip: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.POSTAL_CODE),
      post: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.POSTAL_TOWN),
      community: GooglePlacesMapper.findComponentLongName(response, GooglePlacesTypes.ADMINISTRATIVE_AREA_LEVEL_3),
    });
  };

  public async getAddressesByInput(input: string, type: AutocompletePlacesTypes, includedRegionCodes?: CountryCode[]): Promise<GoogleAutocompletePrediction[]> {
    const requestOptions: google.maps.places.AutocompleteRequest = {
      input,
      sessionToken: this.getSessionToken(),
      includedRegionCodes: includedRegionCodes?.map(code => code.toLowerCase()),
    };
    const {suggestions,} = await this.googleApi.AutocompleteSuggestion.fetchAutocompleteSuggestions(requestOptions);
    const typesFilter = GooglePlacesMapper.typesForFilter(type);
    const predictions = suggestions
      .map(suggestion => suggestion.placePrediction)
      .filter((prediction): prediction is google.maps.places.PlacePrediction => Boolean(prediction))
      .map(prediction => GoogleAutocompletePrediction.fromPlacePrediction(prediction));

    return predictions
      .filter(row => typesFilter ? intersection(row.types, typesFilter).length > 0 : true);
  };
}

export default new GooglePlacesApi();
